import { DownloadIcon, RefreshCwIcon, RotateCcwIcon, XIcon } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { useDesktopAppUpdate } from "src/components/root/DesktopAppUpdateProvider";
import { Button } from "src/components/ui/button";
import { useNewVersionAvailable } from "src/hooks/useNewVersionAvailable";
import { isTauriRuntime } from "src/lib/runtime/environment";

/**
 * Bottom-left toast that appears once a newer build has been deployed, letting
 * the user refresh on their own terms before they hit a stale lazy-route chunk.
 * Positioned clear of the bottom-center `PendingChangesSaveBar`.
 */
const BrowserNewVersionBanner = () => {
  const isStale = useNewVersionAvailable();
  const [dismissed, setDismissed] = useState(false);

  const handleRefresh = useCallback(() => window.location.reload(), []);
  const handleDismiss = useCallback(() => setDismissed(true), []);

  if (!isStale || dismissed) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-row items-center gap-3 bg-card dark:border dark:border-gray-700 rounded-full shadow-lg px-4 py-2">
      <RefreshCwIcon className="h-4 w-4 text-foreground" />
      <span className="text-sm font-medium text-foreground">A new version is available.</span>
      <Button variant="default" size="sm" onClick={handleRefresh}>
        Refresh
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="flex items-center justify-center h-6 w-6 rounded-full hover:bg-accent transition-colors"
      >
        <XIcon className="h-3.5 w-3.5 text-foreground" />
      </button>
    </div>
  );
};

const DesktopNewVersionBanner = () => {
  const update = useDesktopAppUpdate();
  const [dismissedVersion, setDismissedVersion] = useState<string>();

  useEffect(() => {
    if (update.phase === "readyToRelaunch") setDismissedVersion(undefined);
  }, [update.phase]);

  const visible =
    update.enabled &&
    ["available", "downloading", "readyToRelaunch"].includes(update.phase) &&
    dismissedVersion !== update.availableVersion;
  if (!visible) return null;

  const ready = update.phase === "readyToRelaunch";
  const downloading = update.phase === "downloading";

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-row items-center gap-3 bg-card dark:border dark:border-gray-700 rounded-full shadow-lg px-4 py-2">
      {ready ? (
        <RotateCcwIcon className="h-4 w-4 text-foreground" />
      ) : downloading ? (
        <RefreshCwIcon className="h-4 w-4 animate-spin text-foreground" />
      ) : (
        <DownloadIcon className="h-4 w-4 text-foreground" />
      )}
      <span className="text-sm font-medium text-foreground">
        {ready
          ? `Version ${update.availableVersion} is installed.`
          : downloading
            ? `Installing version ${update.availableVersion}${update.progress === undefined ? "…" : ` — ${update.progress}%`}`
            : `Version ${update.availableVersion} is available.`}
      </span>
      <Button
        variant="default"
        size="sm"
        disabled={downloading}
        onClick={() => void (ready ? update.relaunch() : update.installUpdate())}
      >
        {ready ? "Relaunch" : downloading ? "Installing…" : "Update"}
      </Button>
      {!downloading ? (
        <button
          type="button"
          onClick={() => setDismissedVersion(update.availableVersion)}
          aria-label="Later"
          title="Later"
          className="flex items-center justify-center h-6 w-6 rounded-full hover:bg-accent transition-colors"
        >
          <XIcon className="h-3.5 w-3.5 text-foreground" />
        </button>
      ) : null}
    </div>
  );
};

export const NewVersionBanner = memo(() => {
  if (isTauriRuntime()) return <DesktopNewVersionBanner />;
  return <BrowserNewVersionBanner />;
});

NewVersionBanner.displayName = "NewVersionBanner";
